import { AfterViewInit, Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Subject, of } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, switchMap, takeUntil } from 'rxjs/operators';
import { SuperAdminManualService } from '../../core/backend/academy/services/super-admin-manual.service';
import { AuthenticationService } from '../../core/services/auth.service';

export interface QuickJumpPage {
  label: string;
  /** Parent menu label for sub-pages, shown as a hint. */
  group?: string;
  link: string;
}

export interface QuickJumpResult {
  kind: 'page' | 'academy';
  label: string;
  hint: string;
  link: string;
  queryParams?: Record<string, string | number>;
}

/** Pages whose label (or group label) contains the query, case-insensitive; all pages when empty. */
export function matchPages(pages: QuickJumpPage[], query: string, translate: (key: string) => string): QuickJumpPage[] {
  const term = query.trim().toLowerCase();
  if (!term) {
    return pages;
  }
  return pages.filter((page) => [page.label, page.group]
    .filter((key): key is string => Boolean(key))
    .some((key) => translate(key).toLowerCase().includes(term)));
}

@Component({
  selector: 'app-quick-jump',
  standalone: true,
  imports: [CommonModule, FormsModule, TranslateModule],
  template: `
    <div class="quick-jump" (keydown)="onKeydown($event)">
      <label class="quick-jump-search">
        <span class="isax isax-search-normal-1 fs-1_25rem text-muted"></span>
        <input
          #input
          type="search"
          class="form-control border-0 shadow-none"
          [placeholder]="'SUPER_ADMIN.QUICK_JUMP.PLACEHOLDER' | translate"
          [attr.aria-label]="'SUPER_ADMIN.QUICK_JUMP.PLACEHOLDER' | translate"
          [(ngModel)]="query"
          (ngModelChange)="onQuery($event)"
        />
        <kbd class="quick-jump-kbd">Esc</kbd>
      </label>
      <div class="quick-jump-list" role="listbox">
        @for (group of groups; track group.title) {
          <div class="quick-jump-group">{{ group.title | translate }}</div>
          @for (result of group.items; track result.kind + result.link + result.label) {
            <button
              type="button"
              role="option"
              class="quick-jump-item"
              [class.active]="flat.indexOf(result) === activeIndex"
              [attr.aria-selected]="flat.indexOf(result) === activeIndex"
              (mouseenter)="activeIndex = flat.indexOf(result)"
              (click)="go(result)"
            >
              <span class="isax fs-1_125rem" [ngClass]="result.kind === 'academy' ? 'isax-category' : 'isax-arrow-left-3'"></span>
              <span class="flex-grow-1 text-truncate">{{ result.kind === 'page' ? (result.label | translate) : result.label }}</span>
              <span class="small text-muted text-truncate">{{ result.kind === 'page' ? (result.hint | translate) : result.hint }}</span>
            </button>
          }
        }
        @if (flat.length === 0) {
          <div class="text-center text-muted py-4">{{ 'SUPER_ADMIN.QUICK_JUMP.NO_RESULTS' | translate }}</div>
        }
      </div>
      <div class="quick-jump-foot small text-muted">{{ 'SUPER_ADMIN.QUICK_JUMP.HINT' | translate }}</div>
    </div>
  `,
  styles: [`
    .quick-jump { display: flex; flex-direction: column; max-height: 70vh; }
    .quick-jump-search { display: flex; align-items: center; gap: 8px; padding: 10px 16px; border-bottom: 1px solid var(--bs-border-color); margin: 0; }
    .quick-jump-search input { font-size: 1rem; min-height: 44px; background: transparent; }
    .quick-jump-kbd { white-space: nowrap; font-size: 11px; background: var(--bs-tertiary-bg); color: var(--bs-secondary-color); border-radius: 6px; padding: 2px 6px; }
    .quick-jump-list { overflow-y: auto; padding: 8px; }
    .quick-jump-group { font-size: 12px; font-weight: 600; color: var(--bs-secondary-color); padding: 10px 10px 4px; }
    .quick-jump-item { display: flex; align-items: center; gap: 12px; width: 100%; min-height: 44px; padding: 8px 12px; border: 0; border-radius: 10px; background: none; color: var(--bs-body-color); text-align: start; }
    .quick-jump-item.active { background: rgba(9, 178, 96, 0.1); color: #067a43; }
    .quick-jump-foot { padding: 8px 16px; border-top: 1px solid var(--bs-border-color); }
  `],
})
export class QuickJumpComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('input') input?: ElementRef<HTMLInputElement>;

  /** Set by the opener: the pages this user can open. */
  pages: QuickJumpPage[] = [];

  query = '';
  activeIndex = 0;
  groups: Array<{ title: string; items: QuickJumpResult[] }> = [];
  flat: QuickJumpResult[] = [];

  private academies: QuickJumpResult[] = [];
  private readonly query$ = new Subject<string>();
  private readonly destroy$ = new Subject<void>();

  constructor(
    readonly modal: NgbActiveModal,
    private readonly router: Router,
    private readonly translate: TranslateService,
    private readonly superAdmin: SuperAdminManualService,
    private readonly auth: AuthenticationService,
  ) {}

  ngOnInit(): void {
    this.rebuild();
    this.query$
      .pipe(
        debounceTime(250),
        distinctUntilChanged(),
        switchMap((term) => {
          if (term.trim().length < 2 || !this.auth.hasPermission('super-admin.academies.view')) {
            return of([]);
          }
          return this.superAdmin.listAcademies(1, 6, term).pipe(
            catchError(() => of(null)),
            switchMap((response) => of(response?.data?.items || [])),
          );
        }),
        takeUntil(this.destroy$),
      )
      .subscribe((items) => {
        this.academies = items.map((academy) => ({
          kind: 'academy' as const,
          label: academy.title || '-',
          hint: academy.manager_name || '',
          link: '/academies',
          queryParams: { open: academy.id },
        }));
        this.rebuild();
      });
  }

  ngAfterViewInit(): void {
    setTimeout(() => this.input?.nativeElement.focus());
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onQuery(term: string): void {
    this.activeIndex = 0;
    this.rebuild();
    this.query$.next(term || '');
  }

  onKeydown(event: KeyboardEvent): void {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      const step = event.key === 'ArrowDown' ? 1 : -1;
      this.activeIndex = (this.activeIndex + step + this.flat.length) % Math.max(this.flat.length, 1);
    } else if (event.key === 'Enter' && this.flat[this.activeIndex]) {
      event.preventDefault();
      this.go(this.flat[this.activeIndex]);
    }
  }

  go(result: QuickJumpResult): void {
    this.modal.close();
    this.router.navigate([result.link], { queryParams: result.queryParams });
  }

  private rebuild(): void {
    const pages = matchPages(this.pages, this.query, (key) => this.translate.instant(key))
      .slice(0, 8)
      .map((page) => ({ kind: 'page' as const, label: page.label, hint: page.group || '', link: page.link }));
    this.groups = [
      { title: 'SUPER_ADMIN.QUICK_JUMP.PAGES', items: pages },
      { title: 'SUPER_ADMIN.QUICK_JUMP.ACADEMIES', items: this.academies },
    ].filter((group) => group.items.length > 0);
    this.flat = this.groups.flatMap((group) => group.items);
    if (this.activeIndex >= this.flat.length) {
      this.activeIndex = 0;
    }
  }
}
